import { useEffect } from "react";

/**
 * Lightweight per-route SEO head manager (no external dependency).
 *
 * Sets the document title, meta description, canonical URL, Open Graph / Twitter
 * tags and a robots directive for the current route. Public marketing pages get
 * an indexable canonical; authenticated app, auth and internal routes pass
 * `noindex` so they are never indexed even if linked. Do not rely on this alone
 * for privacy — private routes are also disallowed in robots.txt AND protected
 * server-side.
 */

const SITE_ORIGIN = "https://odinforge.io";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/brand/odin/odin-og.jpg`;
const SITE_NAME = "ODIN";

export interface SeoProps {
  /** Page title (a " — ODIN" suffix is added unless the title already ends with ODIN). */
  title: string;
  description?: string;
  /** Path (e.g. "/features") used to build the canonical URL. Omit for noindex pages. */
  path?: string;
  /** When true, emit robots noindex,nofollow and skip the canonical. */
  noindex?: boolean;
  ogImage?: string;
}

function upsertMeta(attr: "name" | "property", key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeLink(rel: string): void {
  document.head.querySelector(`link[rel="${rel}"]`)?.remove();
}

/** Render-less component: apply SEO head tags for the current route. */
export function Seo({ title, description, path, noindex, ogImage }: SeoProps): null {
  useEffect(() => {
    const fullTitle = /odin$/i.test(title.trim()) ? title : `${title} — ${SITE_NAME}`;
    document.title = fullTitle;

    if (description) upsertMeta("name", "description", description);

    const robots = noindex ? "noindex, nofollow" : "index, follow";
    upsertMeta("name", "robots", robots);

    if (!noindex && path) {
      const canonical = `${SITE_ORIGIN}${path === "/" ? "/" : path.replace(/\/$/, "")}`;
      upsertLink("canonical", canonical);
      upsertMeta("property", "og:url", canonical);
      upsertMeta("property", "og:type", "website");
      upsertMeta("property", "og:site_name", SITE_NAME);
      upsertMeta("property", "og:title", fullTitle);
      upsertMeta("name", "twitter:title", fullTitle);
      if (description) {
        upsertMeta("property", "og:description", description);
        upsertMeta("name", "twitter:description", description);
      }
      upsertMeta("property", "og:image", ogImage ?? DEFAULT_OG_IMAGE);
      upsertMeta("name", "twitter:image", ogImage ?? DEFAULT_OG_IMAGE);
      upsertMeta("name", "twitter:card", "summary_large_image");
    } else {
      // Private/noindex pages must not advertise a canonical.
      removeLink("canonical");
    }
  }, [title, description, path, noindex, ogImage]);

  return null;
}
